/**
 * Hafıza sekmesi — personanın yaşayan belleğini görüntüle ve yönet.
 *
 * Hafıza üç yolla dolar: tartışma sonrası otomatik damıtma, öz-çıkarım
 * (reflection) ve elle ekleme. Bu ekran hepsini aynı listede gösterir ama
 * `kind` ile ayırır, çünkü geri getirme davranışları farklıdır (bkz.
 * `persona_memory.py::compile_prompt_block`).
 *
 * Arşiv (bi-temporal) ayrımı özellikle görünür kılınmıştır: silinen bir anı
 * gerçekten silinmez, `validTo` damgalanır. Kullanıcının "sildim ama hâlâ
 * duruyor" diye şaşırmaması için arşiv ayrı bir filtre ve rozetle sunulur.
 */
import { useCallback, useEffect, useState } from "react";
import {
  MEMORY_KIND_HINTS,
  MEMORY_KIND_LABELS,
  type MemoryKind,
  type PersonaMemory,
  type PersonaMemoryStats,
} from "../../domain/personaLibrary";
import {
  LibraryApiError,
  createMemory,
  deleteMemory,
  forceReflect,
  listMemories,
  patchMemory,
} from "../../transport/personaLibraryClient";
import {
  Btn,
  Chip,
  EmptyState,
  ErrorBanner,
  Field,
  Modal,
  SectionTitle,
  Select,
  TextArea,
  TextInput,
} from "./ui";

const KIND_TONE: Record<MemoryKind, "violet" | "cyan" | "amber" | "green"> = {
  episodic: "cyan",
  semantic: "violet",
  reflection: "amber",
  procedural: "green",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function MemoryCard({
  memory,
  onTogglePin,
  onArchive,
  busy,
}: {
  memory: PersonaMemory;
  onTogglePin: () => void;
  onArchive: () => void;
  busy: boolean;
}) {
  const archived = memory.validTo !== null;
  return (
    <article
      className={[
        "rounded-xl border px-3.5 py-3 transition-colors",
        archived
          ? "border-white/[0.05] bg-white/[0.01] opacity-60"
          : "border-white/[0.08] bg-white/[0.02] hover:border-white/[0.14]",
      ].join(" ")}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <Chip tone={KIND_TONE[memory.kind]}>{MEMORY_KIND_LABELS[memory.kind]}</Chip>
        <Chip title="Generative Agents 'poignancy' — geri getirme skorunu besler">
          önem {memory.importance.toFixed(1)}
        </Chip>
        {memory.isPinned && <Chip tone="amber">sabitli</Chip>}
        {archived && <Chip tone="rose">arşiv</Chip>}
        {memory.accessCount > 0 && (
          <Chip title="Bu anının kaç turda prompt'a girdiği">
            {memory.accessCount}× kullanıldı
          </Chip>
        )}
        <span className="ml-auto text-[10.5px] text-slate-600">
          {formatDate(memory.createdAt)}
        </span>
      </div>

      <p className="text-[12.5px] leading-relaxed text-slate-200">{memory.content}</p>

      {memory.context && (
        <p className="mt-1 text-[11px] italic leading-relaxed text-slate-500">
          {memory.context}
        </p>
      )}

      {memory.keywords.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {memory.keywords.slice(0, 6).map((k) => (
            <span key={k} className="text-[10.5px] text-slate-600">
              #{k}
            </span>
          ))}
        </div>
      )}

      {!archived && (
        <div className="mt-2 flex items-center gap-1.5 border-t border-white/[0.05] pt-2">
          <Btn size="sm" tone="ghost" onClick={onTogglePin} disabled={busy}>
            {memory.isPinned ? "Sabitlemeyi kaldır" : "Sabitle"}
          </Btn>
          <Btn size="sm" tone="ghost" onClick={onArchive} disabled={busy}>
            Arşivle
          </Btn>
        </div>
      )}
    </article>
  );
}

export function MemoryTab({
  personaId,
  stats,
  onStatsStale,
}: {
  personaId: string;
  stats: PersonaMemoryStats;
  onStatsStale: () => void;
}) {
  const [memories, setMemories] = useState<PersonaMemory[]>([]);
  const [kind, setKind] = useState<MemoryKind | "">("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [reflectNote, setReflectNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMemories(await listMemories(personaId, { kind, includeArchived }));
      setError(null);
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Hafıza yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [personaId, kind, includeArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  async function mutate(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onStatsStale();
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "İşlem başarısız.");
    } finally {
      setBusy(false);
    }
  }

  async function handleReflect() {
    setBusy(true);
    setReflectNote(null);
    try {
      const count = await forceReflect(personaId);
      setReflectNote(
        count > 0
          ? `${count} yeni öz-çıkarım üretildi.`
          : "Yeni çıkarım üretilmedi — birleştirilecek yeterli yeni yaşantı yok.",
      );
      await load();
      onStatsStale();
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Çıkarım üretilemedi.");
    } finally {
      setBusy(false);
    }
  }

  const readiness =
    stats.reflectionThreshold > 0
      ? Math.min(100, (stats.pendingImportance / stats.reflectionThreshold) * 100)
      : 0;

  return (
    <div className="space-y-4">
      <SectionTitle
        title="Yaşayan Hafıza"
        hint="Her tartışmadan sonra personanın ne savunduğu ve ne öğrendiği buraya damıtılır. Bir sonraki tartışmada soruyla ilgili olanlar prompt'una geri verilir."
        action={
          <Btn size="sm" tone="outline" onClick={() => setAdding(true)}>
            + Elle Ekle
          </Btn>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {reflectNote && (
        <div className="rounded-lg border border-violet-500/25 bg-violet-500/[0.07] px-3 py-2 text-[12px] text-violet-200">
          {reflectNote}
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-4">
        {(Object.keys(MEMORY_KIND_LABELS) as MemoryKind[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(kind === k ? "" : k)}
            title={MEMORY_KIND_HINTS[k]}
            className={[
              "rounded-xl border px-3 py-2 text-left transition-colors",
              kind === k
                ? "border-violet-500/50 bg-violet-500/10"
                : "border-white/[0.07] bg-white/[0.02] hover:border-white/15",
            ].join(" ")}
          >
            <div className="text-[17px] font-semibold tabular-nums text-white">
              {stats.byKind[k] ?? 0}
            </div>
            <div className="text-[11px] text-slate-400">{MEMORY_KIND_LABELS[k]}</div>
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3.5 py-3">
        <div className="mb-1.5 flex items-baseline justify-between gap-3">
          <span className="text-[12px] font-medium text-slate-300">Çıkarım eşiği</span>
          <span className="text-[11px] tabular-nums text-slate-500">
            {stats.pendingImportance.toFixed(0)} / {stats.reflectionThreshold.toFixed(0)}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all duration-500"
            style={{ width: `${readiness}%` }}
          />
        </div>
        <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
          Son çıkarımdan bu yana biriken önem toplamı eşiği aştığında persona kendi
          deneyimlerinden üst düzey içgörü üretir. Sabit takvimle değil, birikimle
          tetiklenir — çok kullanılan persona daha sık düşünür.
        </p>
        <div className="mt-2">
          <Btn size="sm" tone="outline" onClick={handleReflect} loading={busy}>
            Şimdi Düşündür
          </Btn>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={kind}
          onChange={(e) => setKind(e.target.value as MemoryKind | "")}
          className="w-auto"
        >
          <option value="">Tüm türler</option>
          {(Object.keys(MEMORY_KIND_LABELS) as MemoryKind[]).map((k) => (
            <option key={k} value={k}>
              {MEMORY_KIND_LABELS[k]}
            </option>
          ))}
        </Select>
        <label className="flex cursor-pointer items-center gap-1.5 text-[11.5px] text-slate-400">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="accent-violet-500"
          />
          Arşivi de göster ({stats.archived})
        </label>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[12px] text-slate-500">Yükleniyor…</div>
      ) : memories.length === 0 ? (
        <EmptyState
          title="Henüz hafıza yok"
          description="Bu persona bir tartışmaya katıldığında ne savunduğu ve ne öğrendiği otomatik olarak buraya yazılır. Şimdiden bilmesini istediğin bir şey varsa elle ekleyebilirsin."
        />
      ) : (
        <div className="space-y-2">
          {memories.map((m) => (
            <MemoryCard
              key={m.id}
              memory={m}
              busy={busy}
              onTogglePin={() =>
                void mutate(() => patchMemory(m.id, { isPinned: !m.isPinned }))
              }
              onArchive={() => void mutate(() => deleteMemory(m.id, false))}
            />
          ))}
        </div>
      )}

      <AddMemoryModal
        open={adding}
        onClose={() => setAdding(false)}
        onSubmit={async (payload) => {
          await mutate(() => createMemory(personaId, payload));
          setAdding(false);
        }}
      />
    </div>
  );
}

function AddMemoryModal({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    content: string;
    kind: MemoryKind;
    context?: string;
    importance?: number;
    isPinned?: boolean;
  }) => Promise<void>;
}) {
  const [content, setContent] = useState("");
  const [kind, setKind] = useState<MemoryKind>("semantic");
  const [context, setContext] = useState("");
  const [importance, setImportance] = useState(7);
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (content.trim().length < 10) return;
    setBusy(true);
    try {
      await onSubmit({
        content: content.trim(),
        kind,
        context: context.trim(),
        importance,
        isPinned: pinned,
      });
      setContent("");
      setContext("");
      setPinned(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Hafızaya Ekle"
      subtitle="Personanın en baştan bilmesini istediğin bilgiyi buraya yaz. Tartışmalarda soruyla ilgiliyse hatırlar."
      width="max-w-xl"
      footer={
        <>
          <Btn tone="ghost" onClick={onClose} disabled={busy}>
            Vazgeç
          </Btn>
          <Btn
            tone="primary"
            onClick={submit}
            loading={busy}
            disabled={content.trim().length < 10}
          >
            Ekle
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Tür" hint={MEMORY_KIND_HINTS[kind]}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as MemoryKind)}>
            {(Object.keys(MEMORY_KIND_LABELS) as MemoryKind[]).map((k) => (
              <option key={k} value={k}>
                {MEMORY_KIND_LABELS[k]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="İçerik" required>
          <TextArea
            rows={4}
            value={content}
            maxLength={4000}
            placeholder="ör. Bu pazarda brüt marj hiçbir zaman %12'nin altına inmedi; altına inen teklifleri sorgula."
            onChange={(e) => setContent(e.target.value)}
          />
        </Field>

        <Field label="Bağlam" hint="Opsiyonel. Bu bilginin nereden geldiğini tek cümleyle açıkla.">
          <TextInput
            value={context}
            maxLength={800}
            onChange={(e) => setContext(e.target.value)}
          />
        </Field>

        <Field
          label={`Önem: ${importance}`}
          hint="Yüksek önem, ilgili olmasa bile hatırlanma şansını artırır."
        >
          <input
            type="range"
            min={0}
            max={10}
            step={0.5}
            value={importance}
            onChange={(e) => setImportance(Number(e.target.value))}
            className="w-full accent-violet-500"
          />
        </Field>

        <label className="flex cursor-pointer items-center gap-2 text-[12px] text-slate-300">
          <input
            type="checkbox"
            checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
            className="accent-violet-500"
          />
          Sabitle — her turda mutlaka hatırlansın (skorlamayı atlar)
        </label>
      </div>
    </Modal>
  );
}
